import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CloseOutlined, ReloadOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux/es/hooks/useSelector';
import {
  setActiveMenu,
  removeFromMenu,
  setRefetch,
  addMenu,
} from '../redux/slices/menu';
import { shallowEqual, useDispatch } from 'react-redux';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { findOrCreateMenuItem } from '../helpers/menu-utils';
import { data as allRoutes } from '../configs/menu-config';
import { sanitizeUserUrls } from '../helpers/role-access';

const TabMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const menu = useSelector((state) => state.menu, shallowEqual);
  const { user } = useSelector((state) => state.auth, shallowEqual);
  const { theme } = useSelector((state) => state.theme, shallowEqual);

  const selectTab = (item) => {
    dispatch(setActiveMenu(item));
    navigate(`/${item.url}`);
  };

  const deleteTab = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    const index = menu.menuItems.indexOf(item);
    if (!index) {
      toast.warning('The menu tab does not close');
      return;
    }

    if (menu.activeMenu?.id !== item.id) {
      dispatch(removeFromMenu({ ...item, nextUrl: menu.activeMenu.url }));
      return;
    }
    const newPath = menu.menuItems.filter((el) => el.url !== item.url);
    const isHaveOtherTabs = newPath.length - 1 > index;
    let currentPath;
    if (isHaveOtherTabs) {
      currentPath = newPath[index];
    } else {
      currentPath = newPath[newPath.length - 1];
    }
    dispatch(removeFromMenu({ ...item, nextUrl: currentPath.url }));
    navigate(`/${currentPath.url}`);
  };

  const reloadPage = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    dispatch(setRefetch(item));
    if (item.id !== menu.activeMenu.id) {
      navigate(`/${item.url}`);
    }
  };

  // Sync menu state from URL on mount and pathname changes
  useEffect(() => {
    if (!user || !pathname) return;

    // Extract URL from pathname (remove leading slash)
    const currentUrl = pathname.startsWith('/') ? pathname.slice(1) : pathname;

    // Check if URL is different from active menu (exact match or sub-path)
    const isSameRoute =
      menu.activeMenu?.url === currentUrl ||
      currentUrl.startsWith(menu.activeMenu?.url + '/');

    // If same route, no need to sync
    if (isSameRoute) {
      return;
    }

    // Get available routes based on user role and theme
    const parcelMode = !!theme.parcelMode && user?.role === 'admin';
    const availableRoutes = parcelMode
      ? allRoutes.parcel
      : sanitizeUserUrls(user?.role, user?.urls || []);

    // Find or create menu item (unified helper)
    const menuItem = findOrCreateMenuItem(
      currentUrl,
      availableRoutes,
      menu.menuItems,
    );

    // Always use addMenu which handles refetch automatically when URL changes
    dispatch(addMenu(menuItem));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, menu.activeMenu?.url]);

  return (
    <div className='navbar-default'>
      <Tabs
        activeKey={menu.activeMenu?.url}
        type='card'
        items={menu.menuItems.map((item) => ({
          key: item.url,
          label: (
            <div onClick={() => selectTab(item)} className='nav-link'>
              <ReloadOutlined
                onClick={(event) => reloadPage(event, item)}
                className='reload-button'
              />
              <span className='nav-text'> {t(item.name)}</span>
              <CloseOutlined
                onClick={(event) => deleteTab(event, item)}
                className='close-button'
              />
            </div>
          ),
        }))}
      />
    </div>
  );
};

export default TabMenu;
